import type { ProgramAudio } from './audio';
import type { LayerTree } from './layer';
import type { Transition } from './scene';

/**
 * The host → Layout Page channel.
 *
 * The host holds the authoritative layer tree and sends it whole on every
 * change. Sending diffs would be smaller, but a Layout Page that missed one
 * message would then stay wrong forever; a full tree is self-correcting, and
 * at this size the difference on the wire is irrelevant.
 */

/** Rooms are namespaced by Live, so two shows cannot cross-talk. */
export function layoutChannel(liveId: string): string {
  return `layout:${liveId}`;
}

/** Rapid edits - dragging a slider - collapse into one send. */
export const LAYOUT_SYNC_DEBOUNCE_MS = 50;

export interface LayoutState {
  layers: LayerTree;
  /**
   * The programme mix. `phase-08.md` sketches this as its own `audio_state`
   * message; it rides inside the layout state instead so one revision covers
   * the whole composition and a receiver cannot end up with a new layer tree
   * and a stale mix.
   */
  audio: ProgramAudio;
  /**
   * Which scene this snapshot was taken from. Receivers do not resolve it -
   * they are sent the layers, never the scene list - but it makes a recording
   * or a log say *what* was on air, not merely what it looked like.
   */
  activeScene?: string;
  /**
   * How to get here from whatever the receiver is currently showing. Absent
   * means "no animation": the first state after connecting, or an edit that
   * the director expects to appear at once.
   *
   * The receiver keeps the tree it already had and cross-fades from it, so the
   * outgoing composition never has to travel a second time.
   */
  transition?: Transition;
  /**
   * Increases with every send. A receiver that gets an older revision out of
   * order discards it instead of flickering back to a stale composition.
   */
  revision: number;
}

/** Host announcing new state. */
export interface StateUpdateMessage {
  type: 'state_update';
  liveId: string;
  state: LayoutState;
}

/**
 * A Layout Page asking for the current state, because it connected after the
 * host's last edit and would otherwise render an empty canvas until the next
 * one.
 */
export interface RequestStateMessage {
  type: 'request_state';
  liveId: string;
}

export type LayoutMessage = StateUpdateMessage | RequestStateMessage;

/** Narrows an unknown payload arriving from a socket or a postMessage. */
export function isLayoutMessage(value: unknown): value is LayoutMessage {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Partial<LayoutMessage>;
  return candidate.type === 'state_update' || candidate.type === 'request_state';
}
